import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import DataTable, { Column } from "@/components/DataTable";
import { LoadingState } from "@/components/LoadingState";
import { dashboardApi } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import type { RecentReading, UnitDashboard } from "@/types";
import { formatDateTime, formatNumber } from "@/utils/format";

export default function UnitDashboardPage() {
  const params = useParams<{ unitId?: string }>();
  const user = useAuthStore((s) => s.user);
  // Unit viewers without a route param fall back to their first granted unit (left as a future enhancement).
  const unitId = params.unitId;
  const [data, setData] = useState<UnitDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!unitId) {
      setLoading(false);
      return;
    }
    dashboardApi.unit(unitId).then((d) => {
      setData(d);
      setLoading(false);
    });
  }, [unitId]);

  if (!unitId) {
    return (
      <AppLayout>
        <p className="text-sm text-slate-500">No unit selected.</p>
      </AppLayout>
    );
  }

  const cols: Column<RecentReading>[] = [
    { key: "date", header: "Date & time", render: (r) => formatDateTime(r.reading_date) },
    { key: "meter", header: "Meter", render: (r) => r.meter_number },
    { key: "utility", header: "Utility", render: (r) => r.utility_type },
    { key: "consumption", header: "Consumption", render: (r) => formatNumber(r.consumption ?? 0, 2) },
  ];

  return (
    <AppLayout>
      <PageHeader title="My unit" subtitle={user?.email ?? ""} />
      {loading || !data ? (
        <LoadingState />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <KpiCard label="Electricity (this month)" value={`${formatNumber(data.monthly_electricity_kwh, 0)} kWh`} accent="brand" />
            <KpiCard label="Water (this month)" value={`${formatNumber(data.monthly_water_kl, 1)} kL`} accent="blue" />
            <KpiCard label="Gas (this month)" value={`${formatNumber(data.monthly_gas_units, 1)}`} accent="amber" />
          </div>
          <div className="card p-5 mb-6">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Consumption trend</h2>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                <Legend />
                <Line type="monotone" dataKey="electricity_kwh" stroke="#0F766E" strokeWidth={2} name="Electricity" />
                <Line type="monotone" dataKey="water_kl" stroke="#0369a1" strokeWidth={2} name="Water" />
                <Line type="monotone" dataKey="gas_units" stroke="#B45309" strokeWidth={2} name="Gas" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Recent readings</h2>
          <DataTable columns={cols} rows={data.recent_readings} emptyMessage="No readings yet" />
        </>
      )}
    </AppLayout>
  );
}
